import { IFormikInputIProps } from "@faire/deprecated-retailer-visitor-shared/components/Form/types";
import { Core } from "@faire/design-tokens";
import { Close } from "@faire/slate/icons/Close";
import { Input } from "@faire/web/slate/Input";
import { format as formatDate } from "date-fns/format";
import { isMatch as isDateMatch } from "date-fns/isMatch";
import { parse as parseDate } from "date-fns/parse";
import { useField } from "formik";
import React, { ChangeEvent, useCallback, useEffect } from "react";
import { NumberFormatBase } from "react-number-format";
import styled from "styled-components";

export const FormikUSDateInput: React.FC<
  Omit<IFormikInputIProps, "blockErrorMsg"> & {
    setTouchOnInputComplete?: boolean;
  }
> = ({ name, trackChange, setTouchOnInputComplete, ...props }) => {
  const internalName = `internal_${name}`;
  const [
    { onChange: onInternalChange, onBlur: onInternalBlur, ...internalField },
    ,
    { setTouched: setInternalTouched, setValue: setInternalValue },
  ] = useField(internalName);
  const [{ onChange, onBlur }, meta, { setValue, setTouched }] = useField(name);

  const format = useCallback((val: string) => {
    if (!val) {
      return "";
    }

    const month = formatMonth(val.substring(0, 2));
    const day = formatDay(val.substring(2, 4));
    const year = val.substring(4, 8);

    return `${month}/${day || "  "}/${year}`;
  }, []);

  const handleInternalValueChange = useCallback(
    async (value: string, track?: boolean) => {
      if (value && value.length === 10 && isDateMatch(value, "P")) {
        const dateValue = parseDate(value, "P", new Date()).getTime();

        if (track) {
          trackChange?.(name, dateValue.toString());
        }
        // need to ensure value is set before enabling validation via setTouched
        await setValue(dateValue);
        setTouched(true);
      } else {
        if (track) {
          trackChange?.(name, "");
        }
        await setValue(undefined);
        setTouched(!setTouchOnInputComplete);
      }
    },
    [name, setTouched, setValue, trackChange, setTouchOnInputComplete]
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      handleInternalValueChange(e.target.value);
      if (!e.target.value && !props.shouldNotModifyTouchedOnClear) {
        setInternalTouched(false);
        setTouched(false);
      }
      onInternalChange(e);
      onChange(e);
    },
    [
      handleInternalValueChange,
      onChange,
      onInternalChange,
      props.shouldNotModifyTouchedOnClear,
      setInternalTouched,
      setTouched,
    ]
  );

  const handleBlur = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      await handleInternalValueChange(value, true);
      trackChange?.(internalName, value);
      onInternalBlur(e);
      onBlur(e);
      if (setTouchOnInputComplete) {
        setInternalTouched(true);
        setTouched(true);
      }
    },
    [
      handleInternalValueChange,
      internalName,
      onBlur,
      onInternalBlur,
      setInternalTouched,
      setTouched,
      trackChange,
      setTouchOnInputComplete,
    ]
  );

  const handleClear = useCallback(() => {
    setInternalValue("");
    setValue(undefined, true);
    trackChange?.(name, "");
  }, [name, setInternalValue, setValue, trackChange]);

  useEffect(() => {
    if (meta.initialValue) {
      setInternalValue(formatDate(meta.initialValue, "P") ?? "");
    }
  }, [meta.initialValue, setInternalValue]);

  return (
    <NumberFormatBase
      {...props}
      {...internalField}
      format={format}
      type="text"
      displayType="input"
      inputMode="numeric"
      defaultValue={undefined}
      customInput={Input}
      error={meta.touched && meta.error}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder="mm/dd/yyyy"
      suffixNode={
        internalField.value ? (
          <IconButton onClick={handleClear}>
            <Close width="12px" color={Core.icon.subdued} />
          </IconButton>
        ) : null
      }
    />
  );
};

const formatMonth = (month: string) => {
  if (month.length === 1 && Number(month[0]) > 1) {
    return `0${month[0]}`;
  } else if (month.length === 2) {
    // set the lower and upper boundary
    if (Number(month) === 0) {
      return "01";
    } else if (Number(month) > 12) {
      return "12";
    }
  }

  return month;
};

const formatDay = (day: string) => {
  if (day.length === 1 && Number(day[0]) > 3) {
    return `0${day[0]}`;
  } else if (day.length === 2) {
    // set the lower and upper boundary
    if (Number(day) === 0) {
      return "01";
    } else if (Number(day) > 31) {
      return "31";
    }
  }

  return day;
};

const IconButton = styled.button.attrs({ type: "button" })`
  display: flex;
  width: 12px;
  height: 12px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
`;
